import { useState, useEffect, useCallback, useMemo } from 'react';
import { projectAPI, getClients } from '../../../../services/api';
import { transformProjectData, transformProjectDataForAPI, RawProject } from '../../../../utils/transformers/projectTransformers';
import { useClient } from '../../../../contexts/ClientContext';
import { usePermissions } from '../../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../../utils/permissions';
import { User } from '../../../../types';

export interface TransformedProject {
  id: string;
  name: string;
  description?: string;
  status: string;
  startDate?: string;
  endDate?: string;
  progress: number;
  infrastructureBudget?: number;
  deliveryBudget?: number;
  infrastructureSpent?: number;
  deliverySpent?: number;
  currency?: string;
  clientId?: string;
  jiraConfigured?: boolean;
  milestoneSource?: string;
  lastSync?: string;
  milestones?: Milestone[];
  has_sow?: boolean;
  sow_google_drive_url?: string | null;
  costTrackingEnabled?: boolean;
  costTagKey?: string | null;
  costTagValue?: string | null;
  partner_name?: string;
}

export interface Milestone {
  id: string;
  name: string;
  description?: string;
  status: string;
  start_date?: string;
  end_date?: string;
  startDate?: string;
  endDate?: string;
  jira_issues?: JiraIssue[];
}

export interface JiraIssue {
  key: string;
  url: string;
  type: string;
  status: string;
  summary: string;
  priority?: string;
}

interface ApiResult {
  success: boolean;
  data?: unknown;
  error?: string;
}

interface NewProjectData {
  name: string;
  description?: string;
  infrastructureBudget: string | number;
  deliveryBudget: string | number;
  startDate?: string;
  endDate?: string;
  clientId?: string;
}

export interface ClientRecord {
  id: string;
  name: string;
  email: string;
}

export const useProjects = (user: User, initialClientFilter: string | null = null) => {
  const { selectedClientId, isAWSUser } = useClient();
  const { hasPermission } = usePermissions();
  const [projects, setProjects] = useState<TransformedProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<TransformedProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientFilter, setClientFilter] = useState<string | null>(initialClientFilter);

  const canActOnClients =
    hasPermission(PERMISSIONS.CLIENTS_VIEW) ||
    hasPermission(PERMISSIONS.CLIENTS_MANAGE) ||
    hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
    hasPermission(PERMISSIONS.PARTNERS_MANAGE);

  const effectiveClientFilter = useMemo(() => {
    if (isAWSUser || canActOnClients) {
      return selectedClientId;
    }
    return clientFilter || null;
  }, [isAWSUser, canActOnClients, selectedClientId, clientFilter]);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result: ApiResult = await projectAPI.getProjects(user.role, user.partner_id ?? null, null, effectiveClientFilter);
      if (result.success) {
        const rawData = result.data as RawProject[];
        const transformedProjects: TransformedProject[] = rawData.map(transformProjectData) as unknown as TransformedProject[];
        setProjects(transformedProjects);

        setSelectedProject(prev => {
          if (prev) {
            const updatedSelectedProject = transformedProjects.find(p => p.id === prev.id);
            if (updatedSelectedProject) {
              return updatedSelectedProject;
            }
            if (transformedProjects.length > 0) {
              return transformedProjects[0];
            }
            return null;
          } else if (transformedProjects.length > 0) {
            return transformedProjects[0];
          }
          return null;
        });
      } else {
        setError(result.error || 'Failed to load projects');
        setProjects([]);
      }
    } catch (err: unknown) {
      console.error('Error loading projects:', err);
      setError('Failed to connect to the server. Please try again later.');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [user.role, user.partner_id, effectiveClientFilter]);

  const createProject = async (projectData: NewProjectData): Promise<ApiResult> => {
    const apiData = transformProjectDataForAPI(projectData as unknown as RawProject);
    const result: ApiResult = await projectAPI.createProject(apiData);
    if (result.success) {
      await loadProjects();
    }
    return result;
  };

  const deleteProject = async (projectId: string): Promise<ApiResult> => {
    const result: ApiResult = await projectAPI.deleteProject(projectId);
    if (result.success) {
      await loadProjects();
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
      }
    }
    return result;
  };

  const updateProjectStatus = async (projectId: string, status: string): Promise<ApiResult> => {
    const result: ApiResult = await projectAPI.updateProjectStatus(projectId, status);
    if (result.success) {
      await loadProjects();
    }
    return result;
  };

  const exportProject = async (projectId: string): Promise<ApiResult> => {
    return projectAPI.exportProject(projectId);
  };

  const refreshProject = useCallback(async (projectId: string): Promise<ApiResult> => {
    try {
      const result: ApiResult = await projectAPI.getProject(projectId);
      if (result.success) {
        const transformedProject: TransformedProject = transformProjectData(result.data as RawProject) as unknown as TransformedProject;

        setProjects(prev => prev.map(p =>
          p.id === projectId ? transformedProject : p
        ));

        setSelectedProject(prev => {
          if (prev?.id === projectId) return transformedProject;
          return prev;
        });

        return { success: true, data: transformedProject };
      }
      return result;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error refreshing project:', err);
      return { success: false, error: message };
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  return {
    projects,
    selectedProject,
    setSelectedProject,
    loading,
    error,
    clientFilter: effectiveClientFilter,
    setClientFilter: isAWSUser ? () => {} : setClientFilter,
    loadProjects,
    createProject,
    deleteProject,
    updateProjectStatus,
    exportProject,
    refreshProject
  };
};

export const useClients = () => {
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const result: ApiResult = await getClients();
      if (result.success) {
        setClients((result.data as ClientRecord[]) ?? []);
      } else {
        console.error('Error loading clients:', result.error);
        setClients([]);
      }
    } catch (err: unknown) {
      console.error('Error loading clients:', err);
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    clients,
    loading,
    loadClients
  };
};
